import { getDatabase, ref, update } from 'firebase/database';
import { formatDate } from './date';
import { FIREBASE_RELATIONS, USER_ROLES } from 'common/components/constant';
import { CallData } from 'common/slice/audioCallSlice';
import { handleError } from './sentry';
import store from 'app/store';
import { Message, Messages, Session } from 'globals/interfaces';
import { chatMessageApi, SendMessagePayload } from 'common/api/message';
import { getParticipantGroupSessionPath } from './firebasePaths';

export const sendMessage = async (
  messagePayload: SendMessagePayload,
  status?: string,
  messageKey?: string,
  participantGroupId?: string | number,
) => {
  try {
    await store.dispatch(chatMessageApi.endpoints.sendMessage.initiate(messagePayload)).unwrap();
    if(status && messageKey) {
      await updateAlvyMessageStatus(
        messagePayload.session_id,
        messageKey,
        status,
        participantGroupId,
      );
    }
  } catch (error) {
    handleError(error);
  }
};

export const updateAlvyMessageStatus = async (
  session_id: number,
  messageKey: string,
  status: string,
  participantGroupId?: string | number,
) => {
  const database = getDatabase();
  const messagePaths = [`${FIREBASE_RELATIONS.SESSION_V2}/${session_id}/messages/${messageKey}`];
  if (participantGroupId !== undefined && participantGroupId !== null) {
    messagePaths.push(
      `${getParticipantGroupSessionPath(participantGroupId, session_id)}/messages/${messageKey}`,
    );
  }
  await Promise.all(
    messagePaths.map((path) => {
      const messageRef = ref(database, path);
      return update(messageRef, { status }).catch((error) => handleError(error));
    }),
  );
};

const deliveredMessageCache = new Set<string>();
const readMessageCache = new Set<string>();
const MAX_MESSAGE_CACHE = 5000;

const trimCache = (cache: Set<string>) => {
  if (cache.size <= MAX_MESSAGE_CACHE) return;
  const excess = cache.size - MAX_MESSAGE_CACHE;
  let removed = 0;
  cache.forEach((key) => {
    if (removed >= excess) return;
    cache.delete(key);
    removed += 1;
  });
};

const buildCacheKey = (sessionId: string | number, messageKey: string | number) =>
  `${sessionId}:${messageKey}`;

export const markCandidateMessagesAsDeliveredInFirebase = (sessionData: Session[] = []) => {
  if (!sessionData?.length) {
    return;
  }
  const database = getDatabase();
  sessionData.forEach((session) => {
    if (!session?.messages) return;
    const participantGroupId = session.participant_group_id;
    if (participantGroupId === undefined || participantGroupId === null) return;
    const updates: Record<string, string> = {};
    Object.entries(session.messages).forEach(([messageKey, message]) => {
      const cacheKey = buildCacheKey(session.session_id, messageKey);
      if (message.role === USER_ROLES.CANDIDATE) {
        if (!message.received_at && !deliveredMessageCache.has(cacheKey)) {
          updates[`${messageKey}/received_at`] = new Date().toISOString();
          deliveredMessageCache.add(cacheKey);
          trimCache(deliveredMessageCache);
        } else if (message.received_at) {
          deliveredMessageCache.delete(cacheKey);
        }
      }
    });
    if (Object.keys(updates).length) {
      const messageRef = ref(database, `${getParticipantGroupSessionPath(participantGroupId, session.session_id)}/messages`);
      update(messageRef, updates).catch((error) => handleError(error));
    }
  });
};

export const markCandidateMessagesAsReadInFirebase = (
  session_id: string,
  messages: Messages,
  participantGroupId?: string | number,
) => {
  if (!messages) {
    return;
  }
  if (participantGroupId === undefined || participantGroupId === null) return;
  const database = getDatabase();
  const updates: Record<string, string> = {};
  Object.entries(messages).forEach(([messageKey, message]) => {
    const cacheKey = buildCacheKey(session_id, messageKey);
    if (message.role === USER_ROLES.CANDIDATE) {
      if (!message.read_at && !readMessageCache.has(cacheKey)) {
        updates[`${messageKey}/read_at`] = new Date().toISOString();
        readMessageCache.add(cacheKey);
        trimCache(readMessageCache);
      } else if (message.read_at) {
        readMessageCache.delete(cacheKey);
      }
    }
  });
  if (Object.keys(updates).length) {
    const messageRef = ref(database, `${getParticipantGroupSessionPath(participantGroupId, session_id)}/messages`);
    update(messageRef, updates).catch((error) => handleError(error));
  }
};

export const hasUnreadMessages = (messages) => {
  return messages.some((item) => (!item.read_at || item.read_at === '') && item.role === 'candidate');
};

export const getDeliveredStatusWithDateTime = (message: Message) => {
  const { read_at, received_at, created_at, role } = message;

  if ([USER_ROLES.CANDIDATE, USER_ROLES.ALVY].includes(role)) {
    return formatDate(created_at);
  }

  return read_at
    ? `${formatDate(created_at)} | Read`
    : received_at
    ? `${formatDate(created_at)} | Delivered`
    : formatDate(created_at);
};

export const getAudioCallStatus = (
  session_id: string,
  audioCallData: Record<string, CallData>,
) => {
  const audioCallSessionData = audioCallData?.[session_id];
  return audioCallSessionData?.status;
};

export const getAudioCallAtttendeeId = (
  session_id: string,
  audioCallData: Record<string, CallData>,
) => {
  const audioCallSessionData = audioCallData?.[session_id];
  return audioCallSessionData?.attendee_id;
};

export const getActiveCallDetails = (audioCallData: Record<string, CallData>) => {
  const activeCall = Object.entries(audioCallData ?? {}).find(([, callData]) => callData.status === 'connected');
  return activeCall ? { ...activeCall[1], session_id: activeCall[0] } : undefined;
};
